import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building, User2Icon, X, Delete, Ban, Eye, AlertTriangle } from 'lucide-react';

const PropertyDialog = ({ 
  propertyDialogOpen, 
  setPropertyDialogOpen, 
  selectedProperty,
  editPropertyData,
  setEditPropertyData,
  handleSaveProperty,
  handleSuspendProperty,
  setDeletePropertyDialogOpen,
  propertyOwner,
  handleViewUser
}) => {
  return (
    <AnimatePresence>
      {propertyDialogOpen && (
        <motion.div
          className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setPropertyDialogOpen(false)} />
          
          <motion.div
            className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-screen overflow-hidden"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 30 }}
          >
            <div className="px-6 py-4 bg-indigo-600 text-white flex justify-between items-center">
              <h3 className="text-lg font-medium">
                Detalles de la Propiedad: {selectedProperty?.titulo}
              </h3>
              <button 
                onClick={() => setPropertyDialogOpen(false)}
                className="p-1 rounded-full hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {selectedProperty && (
              <>
                <div className="p-6 max-h-[80vh] overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Property information */}
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
                      <h4 className="text-md font-semibold text-indigo-800 mb-4 flex items-center gap-2">
                        <Building className="w-4 h-4" />
                        Información de la Propiedad
                      </h4>
                      
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs font-medium text-gray-500">ID</p>
                          <p className="text-sm text-gray-700">{selectedProperty.id}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Título</p>
                          <input
                            type="text"
                            value={editPropertyData.titulo || ''}
                            onChange={(e) => setEditPropertyData({...editPropertyData, titulo: e.target.value})}
                            className="w-full px-3 py-2 text-sm bg-indigo-50/50 border border-indigo-100 rounded-lg text-gray-700 
                                    focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Tipo</p>
                          <select
                            value={editPropertyData.tipo || ''}
                            onChange={(e) => setEditPropertyData({...editPropertyData, tipo: e.target.value})}
                            className="w-full px-3 py-2 text-sm bg-indigo-50/50 border border-indigo-100 rounded-lg text-gray-700 
                                    focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="casa">Casa</option>
                            <option value="departamento">Departamento</option>
                            <option value="terreno">Terreno</option>
                            <option value="oficina">Oficina</option>
                            <option value="local">Local Comercial</option>
                          </select>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Ubicación</p>
                          <textarea
                            value={editPropertyData.ubicacion || ''}
                            onChange={(e) => setEditPropertyData({...editPropertyData, ubicacion: e.target.value})}
                            className="w-full px-3 py-2 text-sm bg-indigo-50/50 border border-indigo-100 rounded-lg text-gray-700 
                                    focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            rows={2}
                          />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Operación</p>
                          <p className="text-sm text-gray-700">
                            {selectedProperty.operacion === 'alquiler' ? 'Alquiler' : 'Venta'}
                          </p>
                        </div>
                        
                        {selectedProperty.operacion === 'alquiler' ? (
                          <div>
                            <p className="text-xs font-medium text-gray-500">Precio Mensual (S/.)</p>
                            <input
                              type="number"
                              value={editPropertyData.precio_mensual || ''}
                              onChange={(e) => setEditPropertyData({...editPropertyData, precio_mensual: parseFloat(e.target.value)})}
                              className="w-full px-3 py-2 text-sm bg-indigo-50/50 border border-indigo-100 rounded-lg text-gray-700 
                                      focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                        ) : (
                          <>
                            <div>
                              <p className="text-xs font-medium text-gray-500">Precio (S/.)</p>
                              <input
                                type="number"
                                value={editPropertyData.precio_soles || ''}
                                onChange={(e) => setEditPropertyData({...editPropertyData, precio_soles: parseFloat(e.target.value)})}
                                className="w-full px-3 py-2 text-sm bg-indigo-50/50 border border-indigo-100 rounded-lg text-gray-700 
                                        focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              />
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">Precio (USD)</p>
                              <input
                                type="number"
                                value={editPropertyData.precio_dolares || ''}
                                onChange={(e) => setEditPropertyData({...editPropertyData, precio_dolares: parseFloat(e.target.value)})}
                                className="w-full px-3 py-2 text-sm bg-indigo-50/50 border border-indigo-100 rounded-lg text-gray-700 
                                        focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              />
                            </div>
                          </>
                        )}
                        
                        <div>
                          <p className="text-xs font-medium text-gray-500">Estado</p>
                          <select
                            value={editPropertyData.estado || ''}
                            onChange={(e) => setEditPropertyData({...editPropertyData, estado: e.target.value})}
                            className="w-full px-3 py-2 text-sm bg-indigo-50/50 border border-indigo-100 rounded-lg text-gray-700 
                                    focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="activo">Activo</option>
                            <option value="suspendido">Suspendido</option>
                            <option value="vendido">Vendido</option>
                            <option value="alquilado">Alquilado</option>
                            <option value="eliminado">Eliminado</option>
                          </select>
                        </div>
                      </div>
                      
                      <div className="mt-5 pt-4 border-t border-gray-100 flex justify-between">
                        <motion.button
                          onClick={() => setDeletePropertyDialogOpen(true)}
                          className="inline-flex items-center px-3 py-2 border border-red-300 text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Delete className="h-4 w-4 mr-1" />
                          Eliminar
                        </motion.button>
                        
                        <motion.button
                          onClick={handleSuspendProperty}
                          className="inline-flex items-center px-3 py-2 border border-amber-300 text-sm leading-4 font-medium rounded-md text-amber-700 bg-white hover:bg-amber-50"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          disabled={selectedProperty.estado === 'suspendido'}
                        >
                          <Ban className="h-4 w-4 mr-1" />
                          Suspender
                        </motion.button>
                      </div>
                    </div>
                    
                    {/* Owner information */}
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
                      <h4 className="text-md font-semibold text-indigo-800 mb-4 flex items-center gap-2">
                        <User2Icon className="w-4 h-4" />
                        Información del Propietario
                      </h4>
                      
                      {propertyOwner ? (
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs font-medium text-gray-500">ID</p>
                            <p className="text-sm text-gray-700">{propertyOwner.id}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500">Nombre</p>
                            <p className="text-sm text-gray-700">{propertyOwner.nombre}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500">Email</p>
                            <p className="text-sm text-gray-700">{propertyOwner.email}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500">Teléfono</p>
                            <p className="text-sm text-gray-700">{propertyOwner.telefono || '-'}</p>
                          </div>
                          
                          <div className="pt-2">
                            <motion.button 
                              className="px-4 py-2 text-sm text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50"
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => handleViewUser(propertyOwner)}
                            >
                              <Eye className="w-4 h-4 mr-1 inline" />
                              Ver Propietario
                            </motion.button>
                          </div>
                        </div>
                      ) : (
                        <div className="py-8 flex flex-col items-center justify-center">
                          <AlertTriangle className="w-10 h-10 text-amber-400 mb-2" />
                          <p className="text-gray-500">No se encontró información del propietario</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                  <motion.button
                    onClick={() => setPropertyDialogOpen(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Cancelar
                  </motion.button>
                  
                  <motion.button
                    onClick={handleSaveProperty}
                    className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-lg shadow-md hover:shadow-lg"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Guardar Cambios
                  </motion.button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PropertyDialog;